/* eslint-disable @typescript-eslint/no-empty-interface */
import React, { useContext, useMemo, useState, useEffect, useImperativeHandle, useRef } from 'react';
import classnames from 'classnames';
import moment from 'moment';
import { Form } from 'tea-component';
import Image from '@gp/components/GeneralImageNew';
import CardSlot from '@gp/components/CardSlot';
import GeneralModal from '@gp/components/GeneralModal';
import COSFileList from '@gp/components/COSFileList';
import KeyUpdatesContents from '@gp/pages/Pipeline/ProjectDetails/ProjectUpdate/components/ProjectUpdateCard/KeyUpdatesContents';
import ActionItemsContents from '@gp/pages/Pipeline/ProjectDetails/ProjectUpdate/components/ProjectUpdateCard/ActionItemsContents';
import RisksContents from '@gp/pages/Pipeline/ProjectDetails/ProjectUpdate/components/ProjectUpdateCard/RisksContents';
import { context } from '@gp/components/Context';
import { reportOperationLog } from '@gp/services/global';
import { deleteProjectUpdate } from '@gp/services/projectUpdate';
import { getUniqueKey, messageTips, getValidDate, formatMaterials2Files, getTencentAvatarSrc } from '@gp/utils/common';
import { getGRActionItemsFormItemLabel } from '../../utils';
import { ALL_REPORT_OPERATION_LOG } from '@gp/constants/operationLogParams';
import type { IBaseProps } from '@home/types/common';
import type { IGRActionItemVO } from '@gp/types/projectUpdate';
import type { ContentType } from '../../index.d';
import { PLATFORM_NAMES, UPLOAD_FOLDER_NAME_MAP } from '@gp/constants/const';

import './index.scss';

const ICON_LIST = [
  {
    isText: true,
    icon: 'pencil-m',
    context: 'Edit',
  },
  {
    isText: true,
    icon: 'trash-can-icon',
    context: 'Delete',
  },
];

interface IProjectUpdateFormCardInstance {}

interface IProjectUpdateFormCardProps extends IBaseProps {
  type: 'add' | 'default'; // 增加的数据还是默认接口的数据
  position: number; // 每个卡片对应的位置，增加的和接口的是分开的
  itemList: any; // 卡片总体接口数据
  selectedUpdationDates: any[]; // 已有的updates的日期，用于卡片顶部选择日期时选择器禁用掉已有的
  handleDeleteCard: (type: 'add' | 'default', position: number) => void;
  editItemHandle: (item: any) => void;
  defaultDataConfirmAccessList?: boolean[]; // 默认数据中每个卡片Confirm按钮是否不禁用（可用）的情况
  setDefaultDataConfirmAccessList?: React.Dispatch<React.SetStateAction<boolean[]>>;
  addDataConfirmAccessList?: boolean[]; // 增加的数据中每个卡片Confirm按钮是否不禁用（可用）的情况
  setAddDataConfirmAccessList?: React.Dispatch<React.SetStateAction<boolean[]>>;
  defaultDataCardStatusList?: boolean[]; // 默认数据中每个卡片是否进入编辑态
  setDefaultDataCardStatusList?: React.Dispatch<React.SetStateAction<boolean[]>>;
  addDataCardStatusList?: boolean[]; // 增加的数据中每个卡片是否进入编辑态
  setAddDataCardStatusList?: React.Dispatch<React.SetStateAction<boolean[]>>;
  resetQueryList: (type: 'add' | 'default') => void; // 重新请求接口获取数据
  setMode: React.Dispatch<React.SetStateAction<'add' | 'edit' | ''>>;
  setCurrentEditList: React.Dispatch<React.SetStateAction<any>>;
  setEditModalVisible: React.Dispatch<React.SetStateAction<boolean>>;
  // 是否可操作，即展示编辑、删除按钮。默认不可操作
  operationable?: boolean;
}

const ProjectUpdateFormCard: React.ForwardRefRenderFunction<
IProjectUpdateFormCardInstance,
IProjectUpdateFormCardProps
> = (props, ref) => {
  const {
    style,
    className,
    type,
    position,
    itemList,
    selectedUpdationDates,
    handleDeleteCard,
    defaultDataConfirmAccessList,
    setDefaultDataConfirmAccessList,
    addDataConfirmAccessList,
    setAddDataConfirmAccessList,
    defaultDataCardStatusList,
    setDefaultDataCardStatusList,
    addDataCardStatusList,
    setAddDataCardStatusList,
    resetQueryList,
    setMode,
    setCurrentEditList,
    setEditModalVisible,
    operationable = false,
    editItemHandle,
    data,
  } = props;

  const { projectDetailData } = useContext<ContentType>(context) || {};
  // 添加的数据这个编辑态权重更高
  const [addEditStatus, setAddEditStatus] = useState(false);
  // 是否处于编辑态
  const [editStatus, setEditStatus] = useState(false);
  const [visible, setVisible] = useState(false);
  // 辨别弹窗显示的文案
  const [visibleType, setVisibleType] = useState<'delete' | 'cancel'>('cancel');
  // 日期
  const [date, setDate] = useState('');
  const [lastDate, setLastDate] = useState('');

  const [keyUpdatesInfo] = useState('');
  const [isDisabledConfirm, setIsDisabledConfirm] = useState(false);
  const [risksList, setRisksList] = useState<any[]>([]);

  const [cardSelectMilestoneId, setCardSelectMilestoneId] = useState('');
  const [cardLastSelectMilestoneId, setCardLastSelectMilestoneId] = useState('');
  const [weekDateValue, setWeekDateValue] = useState<[string, string]>(['', '']); // ['2023-05-21', '2023-05-27']
  const [lastWeekDateValue, setLastWeekDateValue] = useState<[string, string]>(['', '']);
  const [monthDateValue, setMonthDateValue] = useState(''); // 2023-05-01
  const [lastMonthDateValue, setLastMonthDateValue] = useState('');
  const [dateType, setDateType] = useState<'Week' | 'Month'>('Week');
  const [lastDateType, setLastDateType] = useState<'Week' | 'Month'>('Week');
  const [datePickerChangeed, setDatePickerChangeed] = useState(0);
  const [datePickerTabChangeed, setDatePickerTabChangeed] = useState(0);
  const [isRepeatDate, setIsRepeatDate] = useState(false); // 是否选择的重复日期
  const [isRepeatDateType, setIsRepeatDateType] = useState<'Week' | 'Month' | ''>(''); // 重复日期时标记重复的粒度

  // gr workplans数据日期降序排序
  const grWorkplansList = useMemo<IGRActionItemVO[]>(() => {
    const { gr_action: grActions } = itemList || {};
    return grActions || [];
  }, [itemList]);

  // workplans数据日期降序排序
  const workplansList = useMemo<IGRActionItemVO[]>(() => {
    const res = itemList?.content?.workplans || null;
    if (Array.isArray(res) && res?.length) {
      try {
        res?.sort((a: any, b: any) => moment(a?.due_date)?.valueOf() - moment(b?.due_date)?.valueOf());
      } catch (error) {
        console.error(error);
      }
    }
    return res;
  }, [itemList]);

  // 日期筛选器报错状态
  const isErrorStatusDatePicker = useMemo<boolean>(
    () => isRepeatDate && isRepeatDateType === dateType,
    [isRepeatDate, isRepeatDateType, dateType],
  );

  // 状态发生改变，通知父组件，判断Add Updates按钮是否禁用
  useEffect(() => {
    if (type === 'add') {
      const v: boolean[] =
        addDataCardStatusList?.map((t: boolean, i: number) => {
          if (i === position) {
            return addEditStatus;
          }
          return t;
        }) || [];
      setAddDataCardStatusList?.([...v]);
    } else {
      const v: boolean[] =
        defaultDataCardStatusList?.map((t: boolean, i: number) => {
          if (i === position) {
            return editStatus;
          }
          return t;
        }) || [];
      setDefaultDataCardStatusList?.([...v]);
    }
  }, [editStatus, addEditStatus]);

  const handleDeleteClick = () => {
    setVisible(true);
    setVisibleType('delete');
  };

  // 点击弹窗确认按钮
  const handleModalConfirmClick = async () => {
    if (visibleType === 'delete') {
      if (type === 'add') {
        handleDeleteCard(type, position);
        setVisible(false);
        setAddEditStatus(false);
        setEditStatus(false);
        return;
      }
      const params = {
        project_id: projectDetailData?.project_id,
        id: itemList.id,
        update_id: itemList?.update_id,
      };
      const res = await deleteProjectUpdate(params);
      if (res?.status === 200 && res?.data?.code === 200) {
        handleDeleteCard(type, position);
        setVisible(false);
        setAddEditStatus(false);
        setEditStatus(false);
        // 重新请求数据
        resetQueryList(type);
        // @ts-ignore
        reportOperationLog(ALL_REPORT_OPERATION_LOG.DETAIL_PROJECT_UPDATE_DELETE_SUBMIT, 'button');
      } else {
        messageTips('error', 'Delete failed. Please check the network');
      }
      // 确认取消
    } else if (visibleType === 'cancel') {
      setDate(lastDate);
      setCardSelectMilestoneId(cardLastSelectMilestoneId);
      setWeekDateValue(lastWeekDateValue);
      setMonthDateValue(lastMonthDateValue);
      setDateType(lastDateType);
      setDatePickerChangeed(0);
      setDatePickerTabChangeed(0);
      setIsRepeatDate(false);
      setIsRepeatDateType('');

      setVisible(false);
      setAddEditStatus(false);
      setEditStatus(false);
    } else {
      console.error(`type:${visibleType}类型无效!`);
    }
  };

  useEffect(() => {
    if (itemList?.is_add_item) {
      setAddEditStatus(true);
    }
    const date = itemList?.date || '';
    setDate(date);
    setLastDate(date);
    setCardSelectMilestoneId(itemList?.milestone_id);
    setCardLastSelectMilestoneId(itemList?.milestone_id);
    if (itemList?.granularity === 2 && date?.length > 1) {
      const a: [string, string] = [
        date?.split('-')?.[0]?.replaceAll('.', '-') || '',
        date?.split('-')?.[1]?.replaceAll('.', '-') || '',
      ];
      setLastWeekDateValue(a);
    } else {
      const a = date && date !== '-' ? `${date?.replaceAll('.', '-')}-01` : '';
      itemList?.granularity === 1 && setLastMonthDateValue(a);
    }
    const initDateType = itemList?.granularity === 1 ? 'Month' : 'Week';
    setDateType(initDateType);
    setLastDateType(initDateType);

    setIsDisabledConfirm(!date || !itemList?.content?.key_updates || isErrorStatusDatePicker || !cardSelectMilestoneId);

    const _riskList = itemList?.content?.risks;
    if (_riskList?.length) {
      const newRisksList = _riskList?.map((t: any) => ({
        ...t,
        key: getUniqueKey(),
      }));
      setRisksList(newRisksList);
    }
  }, [itemList]);

  useEffect(() => {
    if (type === 'add') {
      const _accessList: boolean[] =
        addDataConfirmAccessList?.map((t: boolean, i: number) => {
          if (i === position) {
            return !!date;
          }
          return t;
        }) || [];
      setAddDataConfirmAccessList?.(_accessList);
    } else if (type === 'default') {
      const _accessList: boolean[] =
        defaultDataConfirmAccessList?.map((t: boolean, i: number) => {
          if (i === position) {
            return !!date;
          }
          return t;
        }) || [];
      setDefaultDataConfirmAccessList?.(_accessList);
    }

    if (!date || !keyUpdatesInfo?.trim() || isErrorStatusDatePicker || !cardSelectMilestoneId) {
      setIsDisabledConfirm(true);
      return;
    }
    isDisabledConfirm && setIsDisabledConfirm(false);
  }, [date, keyUpdatesInfo]);

  // 根据date整理组件入参dateValue
  useEffect(() => {
    if (date?.indexOf('-') !== -1 && date?.length > 1) {
      const a: [string, string] = [
        date?.split('-')?.[0]?.replaceAll('.', '-') || '',
        date?.split('-')?.[1]?.replaceAll('.', '-') || '',
      ];
      const b = 'Week';
      setWeekDateValue(a);
      setDateType(b);
    } else {
      const flag = date && date !== '-';
      const a = flag ? `${date?.replaceAll('.', '-')}-01` : '';
      const b = flag ? 'Month' : 'Week';
      setMonthDateValue(a);
      setDateType(b);
    }
  }, [date]);

  // 日期组件触发onChange，判断日期是否有重复
  useEffect(() => {
    if (!datePickerChangeed) return;
    // 选中日期中过滤掉当前卡片上次选择的日期
    const filterSelectedUpdationDates = selectedUpdationDates?.filter((t: string) => t !== lastDate);
    const flag = filterSelectedUpdationDates?.includes(date) || false;
    setIsRepeatDate(flag);
    setIsRepeatDateType(flag ? dateType : '');
    setIsDisabledConfirm(flag || !keyUpdatesInfo?.trim() || !cardSelectMilestoneId);
  }, [datePickerChangeed]);

  // 日期组件tab切换时
  useEffect(() => {
    if (!datePickerTabChangeed) return;
    if (
      (dateType === 'Week' && !weekDateValue?.[0] && !weekDateValue?.[1]) ||
      (dateType === 'Month' && !monthDateValue)
    ) {
      setIsDisabledConfirm(true);
    }
    if (dateType === 'Week' && weekDateValue?.[0] && weekDateValue?.[1]) {
      setDatePickerChangeed(Date.now());
      setDate(`${weekDateValue?.[0]?.replaceAll('-', '.')}-${weekDateValue?.[1]?.replaceAll('-', '.')}`);
    }
    if (dateType === 'Month' && monthDateValue) {
      setDatePickerChangeed(Date.now());
      setDate(monthDateValue?.replaceAll('-', '.')?.slice(0, 7));
    }
  }, [datePickerTabChangeed]);

  useImperativeHandle(ref, () => ({}));

  return (
    <>
      <CardSlot
        style={style}
        className={classnames(
          'dbg-detail-project-update-tab-content dbg-initial-line-height dbg-card-slot--std',
          className,
        )}
        iconList={operationable ? ICON_LIST : []}
        getChange={async (value: string) => {
          if (value === 'pencil-m') {
            setMode('edit');
            setCurrentEditList(itemList);
            editItemHandle(itemList);
            // setEditModalVisible(true);
          } else if (value === 'trash-can-icon') {
            handleDeleteClick();
          }
        }}
        title={
          <div className="dbg-detail-project-justify">
            <div className="card-top">
              <div>
                {itemList?.granularity === 2 ? 'Weekly' : 'Monthly'}
                &nbsp;Update&nbsp; (
                {itemList?.granularity === 2
                  ? (date?.slice(0, 11) + date?.slice(16))?.replace('-', ' ~ ')?.replaceAll('.', '-') || '-'
                  : date?.replace('.', '-') || '-'}
                )
              </div>
            </div>
          </div>
        }
      >
        <div className="dbg-detail-project-update-card-content">
          <div className="dbg-detail-project-update-card-body gp-inital-list-style">
            <div className="left-content no-max-width">
              <Form
                className={classnames('dbg-project-updates-form', { 'more-width': grWorkplansList?.length })}
                readonly
                layout="fixed"
                fixedLabelWidth={grWorkplansList?.length > 0 ? 208 : 132}
              >
                {itemList?.content?.key_updates?.length > 0 && (
                  <Form.Item label="Key Updates">
                    <Form.Text>
                      <KeyUpdatesContents data={itemList?.content?.key_updates || ''} />
                    </Form.Text>
                  </Form.Item>
                )}
                {grWorkplansList?.length > 0 && (
                  <Form.Item
                    className="dbg-project-updates-form__gr-action-items-form-item"
                    label={getGRActionItemsFormItemLabel(itemList?.gr_action_milestone)}
                  >
                    <Form.Text>
                      <ActionItemsContents isGRActionItems data={grWorkplansList} />
                    </Form.Text>
                  </Form.Item>
                )}
                {workplansList?.length > 0 && (
                  <Form.Item label="Other Action Items">
                    <Form.Text>
                      <ActionItemsContents data={workplansList} />
                    </Form.Text>
                  </Form.Item>
                )}
                {risksList?.length > 0 && (
                  <Form.Item label="Risks">
                    <Form.Text>
                      <RisksContents risks={risksList} />
                    </Form.Text>
                  </Form.Item>
                )}
                {itemList?.material?.length > 0 && (
                  <Form.Item label="Material">
                    <Form.Text>
                      <COSFileList
                        domain="updates_materials"
                        urlSearchParams={{
                          project_id: projectDetailData?.project_id || '',
                          project: projectDetailData?.project || '',
                          update_id: itemList?.update_id || '',
                        }}
                        logKeys={{
                          previewLogKey: 'DETAIL_UPDATES_GR_DOCUMENT_PREVIEW_SUBMIT',
                          downloadLogKey: 'DETAIL_UPDATES_GR_DOCUMENT_DOWNLOAD_SUBMIT',
                          shareLogKey: 'DETAIL_UPDATES_GR_DOCUMENT_SHARE_SUBMIT',
                        }}
                        files={formatMaterials2Files(itemList?.material)}
                        uploadFolderName={UPLOAD_FOLDER_NAME_MAP.updatesMaterial}
                      />
                    </Form.Text>
                  </Form.Item>
                )}
              </Form>
            </div>
          </div>
          <div className="dbg-detail-project-update-card-footer font14">
            <div className="user-name-flex">
              <Image
                className="user-name-img"
                domain="user"
                width={24}
                height={24}
                src={getTencentAvatarSrc(itemList?.update_user)}
              />
              <span className="user-name">{itemList?.update_user || '-'}</span>
              <i className="dbg-vertical-divider"></i>
              <span className="user-name-label">Updated on&nbsp;</span>
              <span className="user-name-date">{getValidDate(itemList?.update_time, 'YYYY-MM-DD')}</span>
            </div>
          </div>
        </div>
      </CardSlot>

      <GeneralModal
        className="tea-dialog-media"
        visible={visible}
        isFont14={false}
        isFooter
        // @ts-ignore
        modalSize={500}
        onConfirm={handleModalConfirmClick}
        onClose={() => setVisible(false)}
        content={
          <div className="gp-detail-project-update-modal">
            <div className="gp-detail-project-update-modal-title">
              {visibleType === 'cancel' ? 'Are you sure to cancel?' : 'Are you sure to delete the project update?'}
            </div>
            <div className="gp-detail-project-update-modal-info">
              {visibleType === 'cancel'
                ? 'The edited information will not be saved after cancellation.'
                : 'Edit information will not be retained after deletion!'}
            </div>
          </div>
        }
      />
    </>
  );
};

export default React.memo(React.forwardRef(ProjectUpdateFormCard));
